import React, { useState, useEffect, useRef } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Button, Table, Tag, Select, Pagination } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faBars } from "@fortawesome/free-solid-svg-icons";
import ShopPopup from "./popup";
import axios from "../../services/axiosConfig";
import Helper from "../../services/Helper";
import Notification from "../../services/Notification";

// ✅ DND KIT
import { DndContext, closestCenter } from "@dnd-kit/core";

import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

// ✅ Sortable Row
const SortableRow = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props["data-row-key"] });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(props.children, (child, index) => {
        // add drag handle only in first column
        if (index === 0) {
          return (
            <td {...listeners} style={{ cursor: "grab" }}>
              <FontAwesomeIcon icon={faBars} style={{ marginRight: 8 }} />
              {child.props.children}
            </td>
          );
        }
        return child;
      })}
    </tr>
  );
};

const List = () => {
  const [openPopup, setOpenPopup] = useState(false);
  const [details, setDetails] = useState();
  const [pageDetails, setPageDetails] = useState([]);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(1);
  const [updatedAt, setUpdatedAt] = useState();
  const timerId = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await axios.get("/category-list", {
          params: { limit, page },
        });

        if (data && data.data) {
          // ✅ ensure sorted
          const sorted = (data.data.data || []).sort(
            (a, b) => a.order - b.order,
          );
          setPageDetails(sorted);
          setCount(data.data.count);
        }
      } catch (error) {
        Helper.ErrorHanlder(error);
      }
    })();
  }, [limit, page, updatedAt]);

  // ✅ DRAG END
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = pageDetails.findIndex((item) => item.id === active.id);
    const newIndex = pageDetails.findIndex((item) => item.id === over.id);

    const newData = arrayMove(pageDetails, oldIndex, newIndex);

    const updated = newData.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    setPageDetails(updated);
    updateOrderBulk(updated);
  };

  // ✅ BULK UPDATE API CALL
  const updateOrderBulk = async (list) => {
    try {
      console.log("Updated Order List: ", list);
      await axios.patch("/update-category-order-bulk", {
        categories: list.map((item) => ({
          id: item.id,
          order: item.order,
        })),
      });

      Notification.success("Order updated successfully");
    } catch (err) {
      Helper.ErrorHanlder(err);
    }
  };

  const columns = [
    {
      title: "",
      dataIndex: "order",
    },
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Discount (Prime Member)",
      dataIndex: "discount",
      render: (discount) => discount + "%",
    },
    {
      title: "Web",
      dataIndex: "web",
      render: (text, record) => (
        <Tag
          role="button"
          color={record.web === false ? "#f50" : "#87d068"}
          onClick={() => updateWebStatus(record)}
        >
          {record.web ? "ACTIVATE" : "DEACTIVATE"}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "active",
      render: (text, record) => (
        <>
          <Tag
            role="button"
            color={record.active === false ? "#f50" : "#87d068"}
            onClick={() => updateStatus(record)}
          >
            {record.active ? "ACTIVATE" : "DEACTIVATE"}
          </Tag>
          <FontAwesomeIcon
            role="button"
            icon={faEdit}
            onClick={() => {
              setDetails(record);
              setOpenPopup(true);
            }}
          />
        </>
      ),
    },
  ];

  function closeModelPopup(data) {
    setOpenPopup(false);
    if (data) {
      setUpdatedAt(Date.now());
      Notification.success(`${details ? "Updated" : "Created"} Successfully`);
    }
  }

  const updateStatus = async (details) => {
    try {
      let obj = { id: details.id, active: !details.active };
      await axios.post("/category-create", obj);
      setUpdatedAt(Date.now());
      Notification.success("Status updated successfully");
    } catch (error) {
      Helper.ErrorHanlder(error);
    }
  };

  const updateWebStatus = async (details) => {
    try {
      let obj = { id: details.id, web: !details.web };
      await axios.patch("/update-web-status-category", obj);
      setUpdatedAt(Date.now());
      Notification.success("Status updated successfully");
    } catch (error) {
      Helper.ErrorHanlder(error);
    }
  };

  const handleChange = (value) => {
    setPage(1);
    setLimit(value);
  };

  return (
    <Container>
      <Row>
        <Col md={12}>
          <Button
            type="primary"
            className="addnew-btn"
            onClick={() => {
              setDetails("");
              setOpenPopup(true);
            }}
            style={{
              marginBottom: "20px",
            }}
          >
            Add New
          </Button>

          {/* ✅ DND WRAPPER */}
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={pageDetails.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              <Table
                components={{
                  body: { row: SortableRow },
                }}
                columns={columns}
                dataSource={pageDetails}
                pagination={false}
                rowKey="id"
              />
            </SortableContext>
          </DndContext>

          <div className="pagination">
            <Select
              defaultValue={limit}
              style={{ width: 120 }}
              onChange={handleChange}
              options={[
                { value: 5, label: "5" },
                { value: 10, label: "10" },
                { value: 20, label: "20" },
                { value: 25, label: "25" },
              ]}
            />
            <Pagination
              current={page}
              pageSize={limit}
              total={count}
              showSizeChanger={false}
              onChange={setPage}
            />
          </div>
        </Col>
      </Row>

      {openPopup && (
        <ShopPopup show={openPopup} close={closeModelPopup} details={details} />
      )}
    </Container>
  );
};

export default List;
